/**
 * Verkleint foto's in de browser voordat ze geüpload worden.
 *
 * ModSecurity op de webserver weigert elke request body groter dan ~12,5 MB
 * (SecRequestBodyLimit 13107200, action Reject) en Apache geeft dan een kale
 * 500 terug — nog voor PHP draait, dus zonder spoor in de Laravel-logs. De
 * bestuurder-stap heeft vier file-inputs; vier gsm-foto's gaan daar makkelijk
 * over. Zie incidentrapport 24 juli 2026.
 *
 * Deze module is de permanente versie van de hotfix die eerst rechtstreeks als
 * public/js/upload-compress.js op de release-map stond. Ze wordt nu via Vite in
 * app.js gebundeld, zodat een deploy ze niet meer wist.
 *
 * Werkt via event-delegatie op `document`, dus ook op velden die Livewire pas
 * later rendert. PDF's en foto's onder 700 KB blijven ongemoeid; kan de browser
 * het formaat niet decoderen, dan gaat het originele bestand gewoon door.
 */

const MAX_EDGE = 2000; // lange zijde in px, ruim genoeg om een rijbewijs te lezen
const QUALITY = 0.82;
const SKIP_UNDER = 700 * 1024; // al klein genoeg, niet aankomen
const FORM_LIMIT = 45 * 1024 * 1024; // marge onder de opgetrokken ModSecurity-limiet (50 MB)

const MESSAGES = {
    nl: 'De gekozen foto\'s zijn samen te groot. Kies ze opnieuw, of maak nieuwe foto\'s met een lagere resolutie.',
    fr: 'Les photos sélectionnées sont trop volumineuses. Choisissez-les à nouveau ou prenez de nouvelles photos en résolution inférieure.',
    en: 'The selected photos are too large together. Please pick them again or take new photos at a lower resolution.',
};

let pending = 0;

function message() {
    const lang = (document.documentElement.lang || 'nl').slice(0, 2).toLowerCase();
    return MESSAGES[lang] || MESSAGES.nl;
}

function setBusy(form, busy) {
    if (!form) return;
    const buttons = form.querySelectorAll('button[type="submit"], input[type="submit"]');
    for (let i = 0; i < buttons.length; i++) {
        buttons[i].disabled = busy;
        buttons[i].style.opacity = busy ? '0.5' : '';
    }
}

/** Decodeert het bestand naar iets dat op een canvas getekend kan worden. */
function decode(file) {
    if (typeof window.createImageBitmap === 'function') {
        try {
            return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () {
                return decodeViaImg(file);
            });
        } catch (e) {
            return decodeViaImg(file);
        }
    }
    return decodeViaImg(file);
}

function decodeViaImg(file) {
    return new Promise(function (resolve, reject) {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = function () {
            URL.revokeObjectURL(url);
            resolve(img);
        };
        img.onerror = function () {
            URL.revokeObjectURL(url);
            reject(new Error('decode failed'));
        };
        img.src = url;
    });
}

function compress(file) {
    return decode(file).then(function (source) {
        const width = source.width || source.naturalWidth;
        const height = source.height || source.naturalHeight;
        if (!width || !height) return file;

        const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * scale);
        canvas.height = Math.round(height * scale);
        canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
        if (source.close) source.close();

        return new Promise(function (resolve) {
            canvas.toBlob(function (blob) {
                if (!blob || blob.size >= file.size) {
                    resolve(file); // compressie hielp niet, origineel houden
                    return;
                }
                resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', {
                    type: 'image/jpeg',
                    lastModified: Date.now(),
                }));
            }, 'image/jpeg', QUALITY);
        });
    });
}

function replaceFile(input, file) {
    const transfer = new DataTransfer();
    transfer.items.add(file);
    input.files = transfer.files;
}

function totalSize(form) {
    const inputs = form.querySelectorAll('input[type="file"]');
    let total = 0;
    for (let i = 0; i < inputs.length; i++) {
        const files = inputs[i].files || [];
        for (let j = 0; j < files.length; j++) total += files[j].size;
    }
    return total;
}

function onChange(event) {
    const input = event.target;
    if (!input || input.tagName !== 'INPUT' || input.type !== 'file') return;

    const file = input.files && input.files[0];
    if (!file) return;
    if (!file.type || file.type.indexOf('image/') !== 0) return; // pdf's ongemoeid
    if (file.size <= SKIP_UNDER) return;

    const form = input.form;
    pending++;
    setBusy(form, true);

    compress(file).then(function (result) {
        if (result !== file) replaceFile(input, result);
    }).catch(function (error) {
        if (window.console) console.error('[upload-compress] mislukt, origineel wordt gebruikt', error);
    }).then(function () {
        pending--;
        if (pending <= 0) {
            pending = 0;
            setBusy(form, false);
        }
    });
}

function onSubmit(event) {
    const form = event.target;
    if (!form || !form.querySelector('input[type="file"]')) return;

    // Vangnet: laat niet versturen zolang er nog gecomprimeerd wordt, of als het
    // totaal ondanks de compressie boven de limiet blijft. Zo krijgt de klant een
    // leesbare melding in plaats van een kale serverfout.
    if (pending > 0 || totalSize(form) > FORM_LIMIT) {
        event.preventDefault();
        event.stopPropagation();
        if (pending === 0) {
            setBusy(form, false);
            window.alert(message());
        }
    }
}

/**
 * Hangt de compressie aan het document. Idempotent: meermaals aanroepen voegt
 * de listeners maar één keer toe.
 */
export function initUploadCompress() {
    if (window.__uploadCompressInit) return;

    // Browser te oud voor DataTransfer of canvas.toBlob: laat alles ongemoeid.
    if (typeof window.DataTransfer !== 'function' || !document.createElement('canvas').toBlob) {
        return;
    }

    window.__uploadCompressInit = true;
    document.addEventListener('change', onChange, true);
    document.addEventListener('submit', onSubmit, true);
}

export default initUploadCompress;
